import {
  columnFilteringFeature,
  createColumnHelper,
  createFilteredRowModel,
  createSortedRowModel,
  filterFn_includesString,
  globalFilteringFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  type ColumnDef,
  type RowData
} from '@tanstack/react-table';
import type { DataType } from './data-types';

/** Per-column options `DataTable` reads from `columnDef.meta`. */
export interface DataTableColumnMeta {
  /** Shows the type under the header (`# int`, `T string`). */
  type?: DataType;
  /** Tints the column: `added` in brand, `retyped` in amber. */
  change?: 'added' | 'retyped' | null;
  /** Monospace cells, for field values, ids and code. */
  mono?: boolean;
  align?: 'left' | 'right';
}

/**
 * What every `DataTable` can do: sort by a header, and filter rows by a
 * search string. TanStack Table v9 bundles only the features registered here.
 */
export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  columnFilteringFeature,
  globalFilteringFeature,
  sortedRowModel: createSortedRowModel(),
  filteredRowModel: createFilteredRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text
  },
  filterFns: { includesString: filterFn_includesString },
  // Type-only: gives `columnDef.meta` its shape without global merging.
  columnMeta: {} as DataTableColumnMeta
});

export type DataTableFeatures = typeof dataTableFeatures;

/**
 * Columns of one table hold different value types, and TanStack's `ColumnDef`
 * is invariant in its value type, so only `any` accepts a mixed list.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyCellValue = any;

/** A column for `DataTable`. Build them with `createDataTableColumns`. */
export type DataTableColumn<TData extends RowData> = ColumnDef<
  DataTableFeatures,
  TData,
  AnyCellValue
>;

/** A typed column helper: `const col = createDataTableColumns<Source>()`. */
export const createDataTableColumns = <TData extends RowData>() =>
  createColumnHelper<DataTableFeatures, TData>();
